/**
 * One item of a list, as a list test's or a list filter's picker offers it
 * (Feature 77 D21, D25): the list's own field found through the
 * capabilities' schemas, then the item and its fields read from `item`,
 * walked into nested objects as the main picker walks a node's fields. A
 * list within the item is offered whole; its own items' fields are listed
 * disabled, one level as list tests are (Feature 82 D12). Earlier nodes are
 * not offered there: an inner rule reads the item only (D22).
 */
import type { FieldSpec } from '@optum/api-client/types';

import { FIRST, type Draft } from './draft.js';
import { ITEM } from './lists.js';
import {
  ITEMS,
  NESTED_WORDS,
  readablePath,
  type ReferenceOption,
  type ReferenceType,
  type Schemas,
} from './references.js';

// A path segment that indexes a list.
const INDEX = /^\d+$/;
// How far the item picker walks into nested objects and lists.
const MAX_DEPTH = 4;
const THE_ITEM = 'the item';

/** The spec of the field a context path names, through its node's schema; undefined when unknown. */
export function specAt(draft: Draft, path: string, schemas: Schemas): FieldSpec | undefined {
  const parts = path.split('.');
  const [node, side, rest] =
    parts[0] === 'input'
      ? [FIRST, 'input', parts.slice(1)]
      : [parts[1] ?? '', parts[2] ?? '', parts.slice(3)];
  const capability =
    node === FIRST
      ? draft.firstCapabilityId
      : draft.steps.find((s) => s.name === node)?.capability_id;
  const schema = schemas.get(capability ?? '');
  if (!schema) return undefined;
  let spec: FieldSpec | undefined = {
    name: side,
    type: 'object',
    children: side === 'result' ? schema.output_fields : schema.input_fields,
  } as FieldSpec;
  for (const segment of rest) {
    if (spec === undefined) return undefined;
    spec =
      INDEX.test(segment) || segment === ITEMS
        ? (spec.item ?? undefined)
        : spec.children?.find((child) => child.name === segment);
  }
  return spec;
}

/** One item path as the picker offers it, `item › need.evidence`, under the list's heading. */
function itemOption(path: string, type: ReferenceType, list: string): ReferenceOption {
  const segments = path === ITEM ? [] : path.slice(ITEM.length + 1).split('.');
  const parts = readablePath(segments);
  return {
    value: path,
    label: `${path} · ${type}`,
    name: parts.at(-1) ?? THE_ITEM,
    where: parts.slice(0, -1).join('.'),
    type,
    node: ITEM,
    side: 'result',
    short: `item › ${parts.join('.') || THE_ITEM}`,
    group: `one item of ${list}`,
  };
}

/** A field inside a list within the item: listed, never offered (D12). */
function nestedOption(path: string, type: ReferenceType, list: string): ReferenceOption {
  return { ...itemOption(path, type, list), disabled: true, note: NESTED_WORDS };
}

/** The item's fields under `prefix`: nested objects walked in, a list within it whole (D12). */
function itemPaths(
  prefix: string,
  fields: readonly FieldSpec[],
  depth: number,
  list: string,
): ReferenceOption[] {
  const found: ReferenceOption[] = [];
  for (const field of fields) {
    const path = `${prefix}.${field.name}`;
    found.push(itemOption(path, field.type, list));
    if (depth >= MAX_DEPTH) continue;
    if (field.children) found.push(...itemPaths(path, field.children, depth + 1, list));
    if (field.item) {
      const items = `${path}.${ITEMS}`;
      const inner = field.item.children ?? [];
      found.push(
        ...(inner.length > 0
          ? inner.map((child) => nestedOption(`${items}.${child.name}`, child.type, list))
          : [nestedOption(items, field.item.type, list)]),
      );
    }
  }
  return found;
}

/** What an inner rule or a pick may read: the item itself, then its fields. */
export function itemReferences(list: FieldSpec | undefined, listName: string): ReferenceOption[] {
  const item = list?.item ?? undefined;
  return [
    itemOption(ITEM, item?.type ?? 'any', listName),
    ...(item?.children ? itemPaths(ITEM, item.children, 1, listName) : []),
  ];
}
